/* ═══════════════════════════════════════════════════════════════
   Org Chart V2 — right-click text-box (note) menu. Same shape as
   EdgeMenu (People Rebuild, Josh grant 2026-08-17): font size stepper
   + shared kit ColorPickerPopover with a dedicated "Default" swatch
   (var(--tx), stored as `undefined`), Delete at the bottom, danger-
   styled, separated. Portal'd via the picker, viewport-clamped,
   dismisses on mousedown-outside (data-orgchart-menu and
   data-colorpicker-panel exempt) + Esc — identical to the other chart
   menus (wired through OrgChartV2's shared dismiss effect).
   ═══════════════════════════════════════════════════════════════ */

import React from 'react';
import { Minus, Plus, Trash2 } from 'lucide-react';
import { Icon } from '../../../../components/ui/Icon';
import { rem } from '../../../../lib/rem';
import type { OrgChartNote } from '../../../../../shared/entities';
import { ColorPickerPopover } from '../../../../components/ui/color-picker';
import { ORG_EDGE_COLORS } from '../../../../lib/palette';
import type { NotePatchFields } from '../hooks/useUndo';

const CUSTOM_SWATCHES = ORG_EDGE_COLORS.slice(1);

const MIN_FONT = 8;
const MAX_FONT = 72;

const sectionLabelStyle: React.CSSProperties = {
  color: 'var(--tm)',
  fontSize: 'var(--font-size-xs)',
  fontWeight: 600,
  textTransform: 'uppercase',
  letterSpacing: '.04em',
  marginBottom: rem(6),
  display: 'block',
};

const stepperButtonStyle: React.CSSProperties = {
  width: 'var(--space-6)',
  height: 'var(--space-6)',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  borderRadius: 'var(--radius-md)',
  border: '1px solid var(--bd)',
  background: 'var(--sf)',
  color: 'var(--tx)',
  cursor: 'pointer',
};

export interface TextBoxMenuProps {
  note: OrgChartNote;
  x: number;
  y: number;
  onUpdateNote: (patch: NotePatchFields) => void;
  onDeleteNote: () => void;
}

export function TextBoxMenu({ note, x, y, onUpdateNote, onDeleteNote }: TextBoxMenuProps) {
  const MENU_W = 236;
  const menuX = Math.min(Math.max(8, x), window.innerWidth - MENU_W - 8);
  const menuY = Math.min(Math.max(8, y), window.innerHeight - 260);
  const isDefault = !note.color;
  const fontSize = note.fontSize || 14;

  const setFontSize = (next: number) => {
    const clamped = Math.round(Math.min(MAX_FONT, Math.max(MIN_FONT, next)));
    if (clamped !== fontSize) onUpdateNote({ fontSize: clamped });
  };

  return (
    <div
      data-orgchart-menu
      role="menu"
      aria-label="Text box options"
      style={{
        position: 'fixed',
        left: menuX,
        top: menuY,
        zIndex: 1000,
        background: 'var(--sa)',
        border: '1px solid var(--bd)',
        borderRadius: 'var(--radius-lg)',
        boxShadow: '0 12px 32px rgba(0,0,0,0.18)',
        padding: rem(10),
        width: MENU_W,
        fontSize: 'var(--font-size-base)',
      }}
    >
      <div style={{ marginBottom: rem(10) }}>
        <span style={sectionLabelStyle}>Font size</span>
        <div style={{ display: 'flex', alignItems: 'center', gap: rem(6) }}>
          <button
            type="button"
            aria-label="Decrease font size"
            title="Decrease font size"
            onClick={() => setFontSize(fontSize - 1)}
            disabled={fontSize <= MIN_FONT}
            style={{ ...stepperButtonStyle, opacity: fontSize <= MIN_FONT ? 0.5 : 1 }}
          >
            <Icon icon={Minus} em={1.0769} aria-hidden="true" />
          </button>
          <input
            type="number"
            aria-label="Font size in pixels"
            min={MIN_FONT}
            max={MAX_FONT}
            value={fontSize}
            onChange={(e) => {
              const v = parseInt(e.target.value, 10);
              if (!Number.isNaN(v)) setFontSize(v);
            }}
            style={{
              width: rem(52),
              height: 'var(--space-6)',
              textAlign: 'center',
              borderRadius: 'var(--radius-md)',
              border: '1px solid var(--bd)',
              background: 'var(--sf)',
              color: 'var(--tx)',
              fontSize: 'var(--font-size-base)',
            }}
          />
          <button
            type="button"
            aria-label="Increase font size"
            title="Increase font size"
            onClick={() => setFontSize(fontSize + 1)}
            disabled={fontSize >= MAX_FONT}
            style={{ ...stepperButtonStyle, opacity: fontSize >= MAX_FONT ? 0.5 : 1 }}
          >
            <Icon icon={Plus} em={1.0769} aria-hidden="true" />
          </button>
        </div>
      </div>

      <div style={{ marginBottom: rem(10) }}>
        <span style={sectionLabelStyle}>Color</span>
        <div style={{ display: 'flex', alignItems: 'center', gap: rem(6) }}>
          <button
            type="button"
            onClick={() => onUpdateNote({ color: undefined })}
            title="Use the theme default text color"
            aria-label="Use default text color"
            aria-pressed={isDefault}
            style={{
              width: 'var(--space-6)',
              height: 'var(--space-6)',
              borderRadius: 'var(--radius-md)',
              border: isDefault ? '2px solid var(--tx)' : '1px solid var(--bd)',
              background: 'var(--sf)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              cursor: 'pointer',
            }}
          >
            <span
              style={{
                width: 'var(--space-4)',
                height: 'var(--space-4)',
                borderRadius: '50%',
                background: 'var(--tx)',
                display: 'block',
              }}
            />
          </button>
          <ColorPickerPopover
            value={note.color || '#8a8f98'}
            onChange={(hex) => onUpdateNote({ color: hex })}
            allowAlpha={false}
            swatches={CUSTOM_SWATCHES}
            aria-label="Text color"
          />
          <span style={{ fontSize: 'var(--font-size-sm)', color: 'var(--tm)' }}>
            {isDefault ? 'Default' : 'Custom'}
          </span>
        </div>
      </div>

      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          gap: 'var(--space-1)',
          borderTop: '1px solid var(--bd)',
          paddingTop: 'var(--space-2)',
        }}
      >
        <button
          onClick={onDeleteNote}
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 'var(--space-2)',
            width: '100%',
            minHeight: 'var(--space-6)',
            padding: rem(6) + ' ' + rem(10),
            borderRadius: 'var(--radius-md)',
            border: '1px solid var(--bd)',
            cursor: 'pointer',
            fontSize: 'calc(var(--font-size-base) * 12 / 13)',
            textAlign: 'left',
            background: 'color-mix(in srgb, var(--dg) 12%, transparent)',
            color: 'var(--dg)',
          }}
        >
          <Icon icon={Trash2} em={1.1667} aria-hidden="true" />
          Delete text box
        </button>
      </div>
    </div>
  );
}
